import './index.scss'

import { format, subDays } from 'date-fns'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'

import Card from '@/components/Card'
import { Export } from '@/components/Icons'
import Page from '@/components/Page'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import DateRangeFilters from '@/pages/SpaceAnalyticsPage/DateRangeFilters'
import PublisherSection from '@/pages/SpaceAnalyticsPage/PublisherSection'
import RelativeDateFilters from '@/pages/SpaceAnalyticsPage/RelativeDateFilters'
import ViewByFilterMap from '@/pages/SpaceAnalyticsPage/RelativeDateFilters/ViewByFilterMap'
import CustomDateFilterTag from '@/pages/SpaceAnalyticsPage/util/CustomDateFilterTag'
import * as actions from '@/pages/SpacePublisherLogsPage/actions'
import { useAppDispatch, useAppSelector } from '@/store/hooks'

import DeviceMetricsSection from './DeviceMetricsSection'
import messages from './messages'
import { getDeviceMetrics, getGeneratedCSVUrls, getPreviousPublisherMetrics, getPublisherMetrics } from './selectors'

const StyledContainer: any = styled.div`
  width: 100%;
`

const AnalyticsPage = () => {
  const { spaceId, appId, correlationId } = useParams<{ spaceId: string; appId: string; correlationId: string }>()
  const history = useHistory()
  const dispatch = useAppDispatch()

  // TODO convert to react query
  const notificationMetrics = useAppSelector(getPublisherMetrics)
  const previousNotificationMetrics = useAppSelector(getPreviousPublisherMetrics)
  const deviceMetrics = useAppSelector(getDeviceMetrics)
  const csvUrls = useAppSelector(getGeneratedCSVUrls)

  const [generateCSV, setGenerateCSV] = useState(false)
  const [isDateRangeQuery, setIsDateRangeQuery] = useState(false)
  const [displayFilterOverlay, setDisplayFilterOverlay] = useState(false)
  const [dateFilterParams, setDateFilterParams] = useState({
    oldest: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    latest: format(new Date(), 'yyyy-MM-dd')
  })

  const getRelativeDateDefaultState = () => {
    const defaultViewByFilter = ViewByFilterMap.getViewByRangeItem('all-time')
    return {
      ...defaultViewByFilter,
      // @ts-expect-error
      oldest: defaultViewByFilter.oldest(),
      latest: new Date().toISOString()
    }
  }

  const [filterParams, setFilterParams] = useState<any>(getRelativeDateDefaultState())

  useEffect(() => {
    if (filterParams.latest) {
      dispatch(actions.resetGeneratedCSVUrls())
      dispatch(actions.fetchSystemConnectorsMetrics(filterParams, correlationId))
      dispatch(actions.fetchToolbarAnalyticsMetrics(filterParams, correlationId))
    }
  }, [dispatch, correlationId, filterParams])

  useEffect(() => {
    if (generateCSV) {
      dispatch(
        actions.fetchNotificationLogs({
          start: filterParams.oldest,
          end: filterParams.latest,
          eventTypes: ['USER_NOTIFICATION_VIEWED', 'USER_NOTIFICATION_DELETED'],
          publisherId: correlationId,
          limit: 1000
        })
      )
      setGenerateCSV(false)
    }
  }, [dispatch, generateCSV, filterParams.oldest, filterParams.latest, spaceId, correlationId])

  useEffect(() => {
    if (csvUrls.urls.urlSigned) {
      window.open(csvUrls.urls.urlSigned)
      return () => {
        dispatch(actions.resetGeneratedCSVUrls())
        dispatch(actions.fetchSystemConnectorsMetrics(filterParams, correlationId))
        dispatch(actions.fetchToolbarAnalyticsMetrics(filterParams, correlationId))
      }
    }
  }, [dispatch, csvUrls.urls.urlSigned, filterParams, correlationId])

  const onApplyDateRangeFilters = (data: any) => {
    setDisplayFilterOverlay(data.displayFilterOverlay)
    setIsDateRangeQuery(data.isDateRangeQuery)
    setFilterParams(data.filterParams)
  }

  const onChangeAnalyticsViewBy = (data: any) => {
    dispatch(actions.resetGeneratedCSVUrls())
    setDisplayFilterOverlay(data.displayFilterOverlay)
    setFilterParams(data.filterParams)
  }

  const onClearDateFilters = (dateFilterParams: any) => {
    setDateFilterParams({ oldest: dateFilterParams.oldest, latest: dateFilterParams.latest })
  }

  const onCloseFilterOverlay = () => {
    setDisplayFilterOverlay(false)
    setFilterParams(getRelativeDateDefaultState())
  }

  const onCancelCustomDateFilter = () => {
    dispatch(actions.resetGeneratedCSVUrls())
    setDisplayFilterOverlay(false)
    setIsDateRangeQuery(false)
    setFilterParams(getRelativeDateDefaultState())
  }

  const goBack = () => history.push(`/space/${spaceId}/appbuilder/${appId}`)
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  return (
    <StyledContainer>
      <Page
        className="workshop-analytics"
        isLoading={notificationMetrics.isLoading}
        loadingMessage={<FormattedMessage {...messages.loading} />}
      >
        <Page.Header
          title={
            <div className="flex">
              <FormattedMessage {...messages.title} />
            </div>
          }
          goBack={goBack}
          backNavTitle={
            <FormattedMessage
              id="AnalyticsPage.WorkshopNotifications.Back"
              defaultMessage={`Back to ${agentsFlagOn ? 'Agent' : 'App'} Instance`}
            />
          }
        />
        <Card isActionInProgress={csvUrls.isLoading}>
          <Card.Header title={<FormattedMessage {...messages.cardTitle} />}>
            <div className="notification-results-options">
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div className="notification-results-options-item" onClick={() => setGenerateCSV(true)}>
                <Export className="notification-results-options-icon" />
                <FormattedMessage {...messages.exportCSV} />
              </div>
            </div>
          </Card.Header>
          <div className="sub-section">
            <FormattedMessage {...messages.cardSubTitle} />
          </div>
          <div className="header-wrapper">
            <div className="sub-section right">
              <RelativeDateFilters
                defaultOption={filterParams.key}
                onChangeAnalyticsViewBy={onChangeAnalyticsViewBy}
                onClearDateFilters={onClearDateFilters}
                dateFilterParams={dateFilterParams}
              />
            </div>
          </div>
          <div className="filter-section">
            <div className="filter-tags">
              {isDateRangeQuery && (
                <CustomDateFilterTag
                  oldest={filterParams.oldest}
                  latest={filterParams.latest}
                  onClose={onCancelCustomDateFilter}
                />
              )}
            </div>
            <div className="filter-divider" />
            {displayFilterOverlay && (
              <div className="filter-overlay">
                <DateRangeFilters
                  onChangeDateRangeFilters={() => {}}
                  dateFilterParams={dateFilterParams}
                  onCloseFilterOverlay={onCloseFilterOverlay}
                  onApplyDateRangeFilters={onApplyDateRangeFilters}
                />
              </div>
            )}
            <div className="filter-notes" />
          </div>
          <PublisherSection
            publisherMetrics={notificationMetrics.metrics}
            previousPublisherMetrics={previousNotificationMetrics.metrics}
            isDateRangeQuery={isDateRangeQuery}
            renderDifferences={false}
            isWorkshopNotification={true}
          />
          <DeviceMetricsSection deviceMetrics={deviceMetrics.metrics} />
        </Card>
      </Page>
    </StyledContainer>
  )
}

AnalyticsPage.displayName = 'AnalyticsPage'
export default AnalyticsPage
